'use client';

import type { LiveRankingItem, LiveRankingType } from '@module/apis/client-side/live/type';
import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import getRankChangeImage from '@module/helpers/ranking/compareRanking';
import getRankingDisplay from '@module/helpers/ranking/getRankingDisplay';
import { showRankChangeRankingType } from '@module/helpers/ranking/rankingFilter';
import useRankingViewModel from '@module/helpers/ranking/useRankingViewModel';
import useModalStore from '@module/stores/useModalStore';
import { MODAL_TYPE_PC } from '@module/types/modal/type';
import { BaseHttpResponseError } from '@module/utils/error/apiErrors';
import formatNumberWithComma from '@module/utils/formatNumberWithComma';
import BaseButton from '@pc/components/atoms/button/BaseButton';
import BookmarkButton from '@pc/components/atoms/button/BookmarkButton';
import LiveBadge from '@pc/components/badge/LiveBadge';
import useGoPlay from '@pc/hooks/play/useGoPlay';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import RankingSkeleton from './RankingSkeleton';

interface Props {
  searchType: LiveRankingType;
}

function RankingTable({ searchType }: Props) {
  const { userInfo, isLogin, isPostAvailable } = useGetLoginInfoQuery();
  const { goPlay } = useGoPlay();
  const { pushModal } = useModalStore();
  const router = useRouter();

  const { data, isLoading, observerRef, fetchErrorCallback } = useRankingViewModel({
    searchType,
    limitPerQuery: 20,
    errorHandler: (error: unknown) => {
      if (error instanceof BaseHttpResponseError) {
        pushModal({
          type: 'CONFIRM_MODAL',
          id: 'useRankingViewModel',
          options: {
            description: error.message,
          },
        });
        return;
      }
      pushModal({
        type: 'CONFIRM_MODAL',
        id: 'useRankingViewModel',
        options: {
          description: '알수없는 오류로 요청이 실패하였습니다.',
        },
      });
    },
  });

  const handleIconClick = (type: 'gift' | 'message', item: LiveRankingItem) => {
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        type: 'AUTH_MODAL',
      });
      return;
    }
    if (userInfo?.authYN === 'N') {
      pushModal({
        type: MODAL_TYPE_PC.IDENTITY_VERIFICATION_MODAL,
        id: 'RankingTable',
      });
      return;
    }

    if (type === 'gift') {
      pushModal({
        type: MODAL_TYPE_PC.GIFT_HEART_MODAL,
        id: 'RankingTable',
        options: { userIdx: item.userIdx, userId: item.userId, userNick: item.userNick },
      });
    } else if (type === 'message') {
      pushModal({
        type: MODAL_TYPE_PC.SEND_MESSAGE_MODAL,
        id: 'RankingTable',
        options: { userIdx: item.userIdx, userId: item.userId, userNick: item.userNick, idMasking: false },
      });
    }
  };

  if (isLoading) return <RankingSkeleton />;
  if (!data?.pages) return <RankingSkeleton />;
  return (
    <>
      <div className='' data-testid='ranking-list'>
        <table className='w-full '>
          <colgroup>
            <col width={60} />
            <col width={120} />
            <col />
            <col />
            <col width={150} />
            <col width={150} />
            <col width={150} />
            <col width={80} />
            {isPostAvailable && <col width={80} />}
            <col width={80} />
          </colgroup>
          <thead>
            <tr className='border-t-2 border-t-borderBg10 border-b border-b-borderBg05 text-[15px] text-colorFg18'>
              <th className='h-[50px]' />
              <th className='font-medium'>{'순위'}</th>
              <th colSpan={2} className='font-medium'>
                {'방송국'}
              </th>
              <th className='font-medium'>{'시청점수'}</th>
              <th className='font-medium'>{'하트점수'}</th>
              <th className='font-medium'>{'추천점수'}</th>
              <th colSpan={1} className='text-nowrap font-medium'>
                {'선물'}
              </th>
              {isPostAvailable && (
                <th colSpan={1} className='text-nowrap font-medium'>
                  {'쪽지'}
                </th>
              )}
              <th colSpan={1} className='text-nowrap font-medium'>
                {'북마크'}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.pages.map((item, index) => {
              if (index >= 200) return null;

              const isLive = !!item?.media;

              return (
                <tr key={item.userId}>
                  <td className='center relative py-[12px]'>
                    <div className='relative w-[56px]'>
                      {isLive && <LiveBadge />}
                      <div
                        className='relative z-0 block h-14 w-14 cursor-pointer overflow-hidden rounded-full border border-borderBg02 bg-white '
                        onClick={() => {
                          if (item.media) {
                            goPlay(item.media);
                          } else {
                            router.push(`/channel/${item.userId}/notice`);
                          }
                        }}
                      >
                        <img
                          className='-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 z-10 h-full w-full object-cover'
                          src={item.userImg}
                          alt={item.userId}
                        />
                      </div>
                    </div>
                  </td>
                  <td className='flex h-[80px] items-center justify-center text-center font-bold font-nanum-square text-colorFg18 text-lg'>
                    {getRankingDisplay(item.rankingNumber)}
                    {showRankChangeRankingType.includes(searchType) &&
                      getRankChangeImage(item.preRankingNumber, item.rankingNumber)}
                  </td>
                  <td className='truncate text-left text-[18px] text-baseFg hover:underline'>
                    <Link prefetch={false} className='block h-full w-full' href={`/channel/${item.userId}/notice`}>
                      {item.userNick}
                    </Link>
                  </td>
                  <td className='truncate text-left text-[18px] text-colorFg18 hover:underline'>
                    <Link prefetch={false} className='h-full w-full' href={`/channel/${item.userId}/notice`}>
                      {item.userId}
                    </Link>
                  </td>
                  <td className='text-center text-[18px] text-colorFg18'>{formatNumberWithComma(item.scoreWatch)}</td>
                  <td className='text-center text-[18px] text-colorFg18'>{formatNumberWithComma(item.scoreSpon)}</td>
                  <td className='text-center text-[18px] text-colorFg18'>{formatNumberWithComma(item.scoreLike)}</td>
                  <td className='text-center'>
                    <div className='flex min-w-10 justify-center'>
                      <BaseButton
                        size='icon'
                        variant='ghost'
                        throttleDelay={1000}
                        className='min-h-[30px] min-w-[30px]'
                        onClick={() => handleIconClick('gift', item)}
                      >
                        <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_gift3.png`} alt='gift' width={22} />
                      </BaseButton>
                    </div>
                  </td>
                  {isPostAvailable && (
                    <td className='text-center'>
                      <div className='flex min-w-10 justify-center'>
                        <BaseButton
                          size='icon'
                          variant='ghost'
                          throttleDelay={1000}
                          className='min-h-[30px] min-w-[30px]'
                          onClick={() => handleIconClick('message', item)}
                        >
                          <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/letter.png`} alt='letter' width={22} />
                        </BaseButton>
                      </div>
                    </td>
                  )}
                  <td className='text-center'>
                    <div className='flex justify-center'>
                      <BookmarkButton
                        isTooltip={false}
                        width={22}
                        userIndex={item.userIdx}
                        initIsBookmark={item.isBookmark}
                        fetchErrorCallback={fetchErrorCallback}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div ref={observerRef} className='h-10 w-full' />
    </>
  );
}

export default RankingTable;
